import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function EmployeeScheduleDialog({ open, onClose, employee, token, user }) {
    const [schedules, setSchedules] = useState([]);
    const [events, setEvents] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [formData, setFormData] = useState({
        eventId: '',
        workDate: new Date().toISOString().split('T')[0],
        startTime: '09:00',
        endTime: '18:00',
        notes: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (open && employee) {
            fetchSchedule();
            fetchEvents();
        }
    }, [open, employee]);

    const fetchSchedule = async () => {
        try {
            const response = await fetch(
                `${API_URL}/api/employees/${employee.id}/schedule`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const data = await response.json();
            if (data.success) {
                setSchedules(data.schedules);
            }
        } catch (error) {
            console.error('Error fetching schedule:', error);
        }
    };

    const fetchEvents = async () => {
        try {
            const response = await fetch(`${API_URL}/api/events`);
            const data = await response.json();
            setEvents(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching events:', error);
            setEvents([]);
        }
    };

    const calculateHours = (start, end) => {
        const [startH, startM] = start.split(':').map(Number);
        const [endH, endM] = end.split(':').map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        return ((endMinutes - startMinutes) / 60).toFixed(2);
    };

    const handleAddShift = async () => {
        setError('');
        setSuccess('');

        try {
            const hours = calculateHours(formData.startTime, formData.endTime);

            const response = await fetch(
                `${API_URL}/api/employees/${employee.id}/schedule`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        ...formData,
                        hours: parseFloat(hours)
                    })
                }
            );

            const data = await response.json();

            if (data.success) {
                setSuccess('Смена добавлена');
                setOpenAddDialog(false);
                fetchSchedule();
                setFormData({
                    eventId: '',
                    workDate: new Date().toISOString().split('T')[0],
                    startTime: '09:00',
                    endTime: '18:00',
                    notes: ''
                });
            } else {
                setError(data.error || 'Ошибка при добавлении смены');
            }
        } catch (error) {
            setError('Ошибка подключения к серверу');
        }
    };

    const getEventName = (eventId) => {
        const event = events.find(e => e.id === eventId);
        return event ? event.title : 'Не указано';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'scheduled': return 'primary';
            case 'worked': return 'success';
            case 'absent': return 'error';
            case 'cancelled': return 'default';
            default: return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'scheduled': return 'Запланировано';
            case 'worked': return 'Отработано';
            case 'absent': return 'Отсутствовал';
            case 'cancelled': return 'Отменено';
            default: return status;
        }
    };

    const canManageSchedule = ['owner', 'admin', 'manager'].includes(user?.role);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                            <CalendarTodayIcon color="primary" />
                            <Typography variant="h6">
                                График работы: {employee?.full_name}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                    {canManageSchedule && (
                        <Box mb={2}>
                            <Button
                                variant="contained"
                                startIcon={<CalendarTodayIcon />}
                                onClick={() => setOpenAddDialog(true)}
                            >
                                Добавить смену
                            </Button>
                        </Box>
                    )}

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell>Мероприятие</TableCell>
                                    <TableCell>Время начала</TableCell>
                                    <TableCell>Время окончания</TableCell>
                                    <TableCell>Часов</TableCell>
                                    <TableCell>Статус</TableCell>
                                    <TableCell>Примечания</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {schedules.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            Нет запланированных смен
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    schedules.map((schedule) => (
                                        <TableRow key={schedule.id}>
                                            <TableCell>
                                                {new Date(schedule.work_date).toLocaleDateString('ru-RU')}
                                            </TableCell>
                                            <TableCell>{getEventName(schedule.event_id)}</TableCell>
                                            <TableCell>{schedule.start_time}</TableCell>
                                            <TableCell>{schedule.end_time}</TableCell>
                                            <TableCell>{schedule.hours} ч</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusText(schedule.status)}
                                                    color={getStatusColor(schedule.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{schedule.notes || '-'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>

            {/* Диалог добавления смены */}
            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Добавить смену</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        fullWidth
                        label="Мероприятие (опционально)"
                        value={formData.eventId}
                        onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
                        margin="normal"
                    >
                        <MenuItem value="">Не указано</MenuItem>
                        {events.map((event) => (
                            <MenuItem key={event.id} value={event.id}>
                                {event.title} - {new Date(event.event_date).toLocaleDateString('ru-RU')}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        type="date"
                        label="Дата"
                        value={formData.workDate}
                        onChange={(e) => setFormData({ ...formData, workDate: e.target.value })}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                    />

                    <Box display="flex" gap={2}>
                        <TextField
                            fullWidth
                            type="time"
                            label="Время начала"
                            value={formData.startTime}
                            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            fullWidth
                            type="time"
                            label="Время окончания"
                            value={formData.endTime}
                            onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Часов: {calculateHours(formData.startTime, formData.endTime)}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Примечания"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddDialog(false)}>Отмена</Button>
                    <Button onClick={handleAddShift} variant="contained">Добавить</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default EmployeeScheduleDialog;
